import {
  ComponentWorkspaceRow,
  ITcComponentRawMaterialInformation,
  ITcProductCertifiedWeightInformation,
  ITcProductComponentInformation,
  ITcProductRawMaterialInformation,
} from "../../../../tc-models/tc-req-dto/tc-certified-product-details.req_dto";
import { roundTcV4Decimal } from "../../../../tc-v4-decimals";

/**
 * Shared "Product WITH Component" derivation helpers.
 *
 * Mirrors the certified-RM resolution used across the without-component steppers
 * (raw-material-info / product-certified-weight / product-geo-info): an RM code is
 * "certified" when its standard (per get-certified-raw-mat-codes) intersects the
 * TC's selected standards (get-base-details-v4 tcStandard).
 */
import { standardCodeFromValue } from "../../incoming-tc-details/incoming-details-apis";

export const makeIsCertifiedRM = (
  certifiedRawMaterialCodes: any[],
  tcStandardList: string[]
) => {
  return (code: string): boolean => {
    if (
      !code ||
      !Array.isArray(certifiedRawMaterialCodes) ||
      certifiedRawMaterialCodes.length === 0
    )
      return false;
    const mapping = certifiedRawMaterialCodes.find(
      (item: any) => item.material_code === code
    );
    const rawStandards: string = mapping?.standard || "";
    if (!rawStandards || rawStandards.toUpperCase() === "NA") return false;
    const standards = rawStandards
      .split("/")
      .map((s: string) => s.trim())
      .filter((s: string) => s && s.toUpperCase() !== "NA");
    if (
      !standards.length ||
      !Array.isArray(tcStandardList) ||
      tcStandardList.length === 0
    )
      return false;

    // Resolve selected TC standard strings to standard codes
    const tcStdCodes = new Set(
      tcStandardList.map((s) => standardCodeFromValue(s).toUpperCase())
    );

    // Business rule: If MMS is selected, RAS, RCS, GRS, RDS, RMS, and RWS are also certified for MMS
    if (tcStdCodes.has("MMS")) {
      ["RAS", "RCS", "GRS", "RDS", "RMS", "RWS"].forEach((std) => {
        tcStdCodes.add(std);
      });
    }

    return standards.some((std) => tcStdCodes.has(std.toUpperCase()));
  };
};

// Standards an RM code is certified for per the RM↔standard mapping
// (get-certified-raw-mat-codes), e.g. RM0005's "RCS/GRS" -> ["RCS", "GRS"].
// Returns [] when the mapping isn't loaded or the code has no certified standard,
// so callers can fall back to an unfiltered list.
export const makeGetRmStandards = (certifiedRawMaterialCodes: any[]) => {
  return (code: string): string[] => {
    if (
      !code ||
      !Array.isArray(certifiedRawMaterialCodes) ||
      certifiedRawMaterialCodes.length === 0
    )
      return [];
    const mapping = certifiedRawMaterialCodes.find(
      (item: any) => item.material_code === code
    );
    const rawStandards: string = mapping?.standard || "";
    if (!rawStandards || rawStandards.toUpperCase() === "NA") return [];
    return rawStandards
      .split("/")
      .map((s: string) => s.trim())
      .filter((s: string) => s && s.toUpperCase() !== "NA");
  };
};

// Component allocated RM weight = componentNetWeight * RM% / 100. A component has no
// supplementary/additives so its material base is its full net shipping weight.
export const getComponentRmWeight = (
  componentNetWeight: number,
  pct: any
): number => {
  const net = Number(componentNetWeight) || 0;
  const p = Number(pct);
  if (Number.isNaN(p)) return 0;
  return (net * p) / 100;
};

/**
 * Component certified-weight group-by: certified RMs grouped by their claims category,
 * allocated weights summed. Returns the array stored on the component for submit.
 */
export const deriveComponentCertifiedWeight = (
  componentNetWeight: number,
  rawMaterials: ITcComponentRawMaterialInformation[],
  isCertifiedRM: (code: string) => boolean,
  ready: boolean
): { tcProductComponentClaimsCategory: string; tcProductComponentCertifiedWeight: number }[] => {
  const map = new Map<string, number>();
  (rawMaterials || []).forEach((r) => {
    const certified = ready
      ? isCertifiedRM(r.tcProductComponentRawMaterialCode)
      : r.certified === true;
    if (!certified) return;
    const claimsCategory = String(r.claimsCategory || "").trim();
    if (!claimsCategory) return;
    const weight = getComponentRmWeight(
      componentNetWeight,
      r.tcProductComponentRawMaterialPercentage
    );
    map.set(claimsCategory, (map.get(claimsCategory) || 0) + weight);
  });
  return Array.from(map.entries()).map(([claimsCategory, weight]) => ({
    tcProductComponentClaimsCategory: claimsCategory,
    tcProductComponentCertifiedWeight: roundTcV4Decimal(weight),
  }));
};

// Total certified weight of one component = sum of its grouped certified weights.
// Tolerates the persisted field name: the backend stores the weight as
// `tcProductComponentClaimsCategoryCertifiedWeight`, while the in-memory shape uses
// `tcProductComponentCertifiedWeight` (so components reloaded for edit still total correctly).
export const getComponentCertifiedWeight = (
  component: ITcProductComponentInformation
): number =>
  (component.tcProductComponentCertifiedWeightInformation || []).reduce(
    (sum, c: any) =>
      sum +
      (Number(
        c?.tcProductComponentCertifiedWeight ??
          c?.tcProductComponentClaimsCategoryCertifiedWeight
      ) || 0),
    0
  );

// Build a workspace row (component + its derived net/certified/non-certified weights).
export const toWorkspaceRow = (
  component: ITcProductComponentInformation
): ComponentWorkspaceRow => {
  const net = Number(component.tcProductComponentNetShippingWeight) || 0;
  const certified = roundTcV4Decimal(getComponentCertifiedWeight(component));
  const nonCertified = roundTcV4Decimal(net - certified);
  return {
    ...component,
    componentNetShippingWeight: net,
    componentCertifiedWeight: certified,
    componentNonCertifiedWeight: nonCertified,
  };
};

// Product total derived from components (live sums for the workspace summary table).
export const deriveProductTotals = (
  components: ITcProductComponentInformation[],
  productSupplementaryWeight = 0
) => {
  const sumNet = (components || []).reduce(
    (s, c) => s + (Number(c.tcProductComponentNetShippingWeight) || 0),
    0
  );
  const sumCertified = (components || []).reduce(
    (s, c) => s + getComponentCertifiedWeight(c),
    0
  );
  const sumNonCertified = sumNet - sumCertified;
  return {
    componentNetSum: roundTcV4Decimal(sumNet),
    // Product Net Shipping (derived) = Σ component net + product supplementary.
    productNetShippingWeight: roundTcV4Decimal(
      sumNet + (Number(productSupplementaryWeight) || 0)
    ),
    productCertifiedWeight: roundTcV4Decimal(sumCertified),
    productNonCertifiedWeight: roundTcV4Decimal(sumNonCertified),
  };
};

/**
 * Deduplicated product-level Raw Material table derived from all components.
 * - union of every component RM code
 * - RM weight = Σ that code's allocated weight across components
 * - RM% = RM weight / (Σ component net) * 100
 * - claimsCategory/certified carried from the component RM rows
 */
export const deriveProductRawMaterials = (
  components: ITcProductComponentInformation[]
): ITcProductRawMaterialInformation[] => {
  const componentNetSum = (components || []).reduce(
    (s, c) => s + (Number(c.tcProductComponentNetShippingWeight) || 0),
    0
  );

  const byCode = new Map<
    string,
    {
      code: string;
      description: string;
      weight: number;
      claimsCategory: string;
      certified: boolean;
    }
  >();

  (components || []).forEach((component) => {
    const net = Number(component.tcProductComponentNetShippingWeight) || 0;
    (component.tcProductComponentRawMaterialInformation || []).forEach((rm) => {
      const code = rm.tcProductComponentRawMaterialCode;
      if (!code) return;
      const allocated = getComponentRmWeight(
        net,
        rm.tcProductComponentRawMaterialPercentage
      );
      const existing = byCode.get(code);
      if (existing) {
        existing.weight += allocated;
        // keep the first non-empty claims category / certified flag seen for this code
        if (!existing.claimsCategory && rm.claimsCategory)
          existing.claimsCategory = String(rm.claimsCategory);
        if (!existing.certified && rm.certified) existing.certified = true;
        if (!existing.description && rm.tcProductComponentRawMaterialCodeDescription)
          existing.description = rm.tcProductComponentRawMaterialCodeDescription;
      } else {
        byCode.set(code, {
          code,
          description: rm.tcProductComponentRawMaterialCodeDescription || "",
          weight: allocated,
          claimsCategory: String(rm.claimsCategory || ""),
          certified: !!rm.certified,
        });
      }
    });
  });

  const rounded = Array.from(byCode.values()).map((entry) => ({
    entry,
    pct:
      componentNetSum > 0
        ? roundTcV4Decimal((entry.weight / componentNetSum) * 100)
        : 0,
  }));

  // Per-row 5dp rounding can still drift the total off 100 by a final unit,
  // which both the step gate and the submit validator reject —
  // fold the rounding residue into the largest row so the derived table sums
  // to exactly 100 (86ajjewmz). Residues beyond ±0.5 are left alone so real
  // component-weight mismatches still surface.
  const total = roundTcV4Decimal(
    rounded.reduce((sum, row) => sum + row.pct, 0)
  );
  const residue = roundTcV4Decimal(100 - total);
  if (
    rounded.length > 0 &&
    componentNetSum > 0 &&
    residue !== 0 &&
    Math.abs(residue) <= 0.5
  ) {
    const largest = rounded.reduce((a, b) => (b.pct > a.pct ? b : a));
    largest.pct = roundTcV4Decimal(largest.pct + residue);
  }

  return rounded.map(({ entry, pct }) => ({
    tcProductRawMaterialCode: entry.code,
    tcProductRawMaterialPercentage: pct,
    tcProductRawMaterialUserSpecificTerm: "",
    tcProductRawMaterialCodeDescription: entry.description,
    claimsCategory: entry.certified ? entry.claimsCategory : "",
    certified: entry.certified,
  }));
};

/**
 * Product certified-weight group-by derived from components: Σ component certified weights
 * per claims category. (Same shape as the without-component product-certified-weight step.)
 */
export const deriveProductCertifiedWeight = (
  components: ITcProductComponentInformation[]
): ITcProductCertifiedWeightInformation[] => {
  const map = new Map<string, number>();
  (components || []).forEach((component) => {
    (component.tcProductComponentCertifiedWeightInformation || []).forEach((c) => {
      const cat = String(c.tcProductComponentClaimsCategory || "").trim();
      if (!cat) return;
      map.set(
        cat,
        (map.get(cat) || 0) + (Number(c.tcProductComponentCertifiedWeight) || 0)
      );
    });
  });
  return Array.from(map.entries()).map(([cat, weight]) => ({
    tcProductClaimsCategory: cat,
    tcProductClaimsCategoryCertifiedWeight: roundTcV4Decimal(weight),
    tcProductCertifiedWeight: roundTcV4Decimal(weight),
    tcProductRawMaterialCodeClaimsCategory: "",
    tcProductRawMaterialCodeClaimsCategoryDescription: "",
  }));
};

/**
 * Reverse of mapComponentToPayload: rebuild the in-memory component shape from a PERSISTED
 * component (get-component-detail-v4 / get-list-component-detail-v4). Used when reopening a saved
 * with-component product for edit. The backend (a) stores the certified weight under
 * `tcProductComponentClaimsCategoryCertifiedWeight` and (b) strips the RM `certified`/`claimsCategory`
 * UI-only extras, so we normalize the weight field and re-derive `certified` (via isCertifiedRM),
 * re-attaching the claims category from the component's certified-weight groups (exact for the
 * common single-claims component; best-effort otherwise — the persisted certified-weight groups,
 * which are exact, still drive every downstream derivation/submit).
 */
export const mapPersistedComponentToInMemory = (
  stored: any,
  componentId: string | undefined,
  isCertifiedRM: (code: string) => boolean
): ITcProductComponentInformation => {
  const certifiedWeightInfo = (
    stored?.tcProductComponentCertifiedWeightInformation ||
    stored?.tcProductComponentStandardInformation ||
    []
  ).map((c: any) => ({
    tcProductComponentClaimsCategory:
      c?.tcProductComponentClaimsCategory ||
      c?.tcProductComponentStandardLabelGrade ||
      "",
    tcProductComponentCertifiedWeight:
      Number(
        c?.tcProductComponentCertifiedWeight ??
          c?.tcProductComponentClaimsCategoryCertifiedWeight ??
          0
      ) || 0,
  }));
  const singleClaims =
    certifiedWeightInfo.length === 1
      ? certifiedWeightInfo[0].tcProductComponentClaimsCategory
      : "";
  // Recover each RM's certified flag + claims category. The persisted RM rows carry only code + %
  // (the UI extras are stripped by the backend whitelist on save), but the component's
  // certified-weight groups (claims -> weight) are exact. Match each RM's allocated weight
  // (net * %/100) to a group — exact matches first, then greedy for any shared-claims remainder —
  // so the reload is faithful even when the (flaky) certified-RM mapping endpoint is unavailable.
  // isCertifiedRM is only a last-resort fallback.
  const componentNet = Number(stored?.tcProductComponentNetShippingWeight) || 0;
  const groups = certifiedWeightInfo.map((c: any) => ({
    claims: c.tcProductComponentClaimsCategory,
    remaining: Number(c.tcProductComponentCertifiedWeight) || 0,
  }));
  const rmRows = (stored?.tcProductComponentRawMaterialInformation || []).map((r: any) => {
    const code = r?.tcProductComponentRawMaterialCode || "";
    const pct = Number(r?.tcProductComponentRawMaterialPercentage) || 0;
    return { code, pct, allocated: (componentNet * pct) / 100, certified: false, claims: "" };
  });
  // Pass 1: exact allocated-weight match to a certified-weight group.
  rmRows.forEach((row: any) => {
    const g = groups.find(
      (gr: any) => gr.remaining > 0.005 && Math.abs(gr.remaining - row.allocated) <= 0.5
    );
    if (g) {
      row.certified = true;
      row.claims = g.claims;
      g.remaining = 0;
    }
  });
  // Pass 2: greedy fill for groups shared by multiple RMs.
  rmRows.forEach((row: any) => {
    if (row.certified) return;
    const g = groups.find(
      (gr: any) => gr.remaining > 0.005 && row.allocated <= gr.remaining + 0.5
    );
    if (g) {
      row.certified = true;
      row.claims = g.claims;
      g.remaining -= row.allocated;
    }
  });
  // Pass 3: certified-RM mapping fallback (only if the weight match was inconclusive).
  rmRows.forEach((row: any) => {
    if (!row.certified && typeof isCertifiedRM === "function" && isCertifiedRM(row.code)) {
      row.certified = true;
      row.claims = singleClaims;
    }
  });
  const rawMaterials = rmRows.map((row: any) => ({
    tcProductComponentRawMaterialCode: row.code,
    tcProductComponentRawMaterialPercentage: row.pct,
    certified: row.certified,
    claimsCategory: row.certified ? row.claims : "",
  }));
  return {
    componentId,
    tcAssociatedProductArticleNo: stored?.tcAssociatedProductArticleNo || "",
    tcProductComponentNo: stored?.tcProductComponentNo || "",
    tcProductComponentDetail: stored?.tcProductComponentDetail || "",
    tcProductComponentNetShippingWeight:
      Number(stored?.tcProductComponentNetShippingWeight) || 0,
    tcProductComponentInfoFiberLength: stored?.tcProductComponentInfoFiberLength || "",
    tcProductComponentInfoYarnCount: stored?.tcProductComponentInfoYarnCount || "",
    tcProductComponentInfoFabricWeightAndConstruct:
      stored?.tcProductComponentInfoFabricWeightAndConstruct || "",
    tcProductComponentInfoFabricWeightOfFinishedProduct:
      stored?.tcProductComponentInfoFabricWeightOfFinishedProduct || "",
    tcProductComponentInfoTechnicalSpecifications:
      stored?.tcProductComponentInfoTechnicalSpecifications || "",
    tcProductComponentInfoAdditional: stored?.tcProductComponentInfoAdditional || "",
    tcProductComponentCertifiedWeightInformation: certifiedWeightInfo,
    tcProductComponentRawMaterialInformation: rawMaterials,
  };
};

// Client-side product id so components can link to the product on first save
// (backend uses req_body.productId when provided). Prefers crypto.randomUUID.
export const generateProductId = (): string => {
  try {
    if (
      typeof crypto !== "undefined" &&
      typeof (crypto as any).randomUUID === "function"
    ) {
      return (crypto as any).randomUUID();
    }
  } catch {
    /* fall through */
  }
  // RFC4122-ish fallback
  return "xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx".replace(/[xy]/g, (c) => {
    const r = (Math.random() * 16) | 0;
    const v = c === "x" ? r : (r & 0x3) | 0x8;
    return v.toString(16);
  });
};
